import React, { useMemo, useState } from 'react';
import { Box } from '@chakra-ui/react';
import { useHistory, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { getQAApolloClient } from './qa-apollo.client';
import QANavigationShell from './QANavigationShell';
import QANavigationContent from './QANavigationContent';
import { Collection, WorkitemStatus } from './types';
import { QualityAssessment as ServiceDeskQualityAssessment } from '../service-desk/service-desk-component';
import { ManagerHomePage } from './manager-home';

// Mirrors service-desk's getAvailableStatuses / getUserRolesFromActiveContext
function getActiveContextRoles(userContext: any): string[] {
  if (!userContext?.activeContext || !userContext?.user?.roles) return [];
  const tenantId = userContext.activeContext.tenantId;
  const matchingRole = userContext.user.roles.find((r: any) => r.tenantId === tenantId);
  return matchingRole ? matchingRole.roles.map((r: any) => r.roleName) : [];
}

function getAvailableStatuses(userContext: any): WorkitemStatus[] {
  const roles = getActiveContextRoles(userContext);
  return roles.includes('cqa_manager')
    ? [WorkitemStatus.ASSIGNED_TO_ME, WorkitemStatus.IN_CQA, WorkitemStatus.CQA_COMPLETE, WorkitemStatus.CQA_APPROVED, WorkitemStatus.CQA_CLOSED]
    : [WorkitemStatus.IN_CQA, WorkitemStatus.CQA_COMPLETE];
}

const QualityAssessmentPage: React.FC = () => {
  const history = useHistory();
  const location = useLocation();

  // Read userContext from Redux (same shape service-desk receives)
  const userContext = useSelector((state: any) => ({
    ...state.ui.context,
    serviceLine: state.ui.serviceLine,
  }));

  const apolloClient = useMemo(() => getQAApolloClient(), []);

  const [searchTerm, setSearchTerm] = useState('');

  const selectedProjectId = useMemo(
    () => new URLSearchParams(location.search).get('projectId'),
    [location.search]
  );

  const isManager = useMemo(() => {
    const roles = getActiveContextRoles(userContext);
    return roles.includes('cqa_manager');
  }, [userContext]);

  const handleHomeClick = () => {
    const params = new URLSearchParams(location.search);
    params.delete('projectId');
    params.delete('status');
    history.replace({ search: params.toString(), state: location.state });
    window.dispatchEvent(new PopStateEvent('popstate', { state: window.history.state }));
  };

  const handleManagerHomeProjectSelected = (projectId: string, status?: string) => {
    const params = new URLSearchParams(location.search);
    params.set('projectId', projectId);
    if (status) {
      params.set('status', status);
    } else {
      params.delete('status');
    }
    history.replace({ search: params.toString(), state: location.state });
    window.dispatchEvent(new PopStateEvent('popstate', { state: window.history.state }));
  };

  const handleProjectSelected = (project: Collection, status?: WorkitemStatus | null) => {
    const params = new URLSearchParams(location.search);

    if (project?.collection_id) {
      params.set('projectId', project.collection_id);
    } else {
      params.delete('projectId');
    }

    // Replicate original behavior: undefined → default to first available status
    const availableStatuses = getAvailableStatuses(userContext);
    const statusToApply =
      status !== undefined && status !== null ? status : (availableStatuses[0] ?? null);

    if (statusToApply) {
      params.set('status', statusToApply);
    } else {
      params.delete('status');
    }

    history.replace({ search: params.toString(), state: location.state });

    // service-desk mounts its own React root (separate BrowserRouter).
    // window.history.replaceState() (used internally by history.replace) does NOT
    // fire a popstate event, so service-desk's router never sees the change.
    // Dispatching popstate manually causes service-desk's BrowserRouter to re-read
    // window.location and update useLocation() → triggers QualityAssessment.tsx's effect.
    window.dispatchEvent(new PopStateEvent('popstate', { state: window.history.state }));
  };

  return (
    <Box display="flex" flexDirection="row" height="100vh" overflow="hidden" width="100%">
      {/* Sidebar — lives in dashboard-ui React tree */}
      <QANavigationShell searchTerm={searchTerm} onSearchChange={setSearchTerm}>
        <QANavigationContent
          apolloClient={apolloClient}
          userContext={userContext}
          onProjectSelected={handleProjectSelected}
          selectedProjectId={selectedProjectId}
          searchTerm={searchTerm}
          isManager={isManager}
          onHomeClick={handleHomeClick}
        />
      </QANavigationShell>

      {/* Content area — ManagerHome for managers with no project selected, otherwise service-desk */}
      <Box flex={1} minWidth={0} overflow="hidden" height="100vh">
        {isManager && !selectedProjectId ? (
          <ManagerHomePage
            apolloClient={apolloClient}
            onProjectSelected={handleManagerHomeProjectSelected}
            searchTerm={searchTerm}
          />
        ) : (
          <ServiceDeskQualityAssessment />
        )}
      </Box>
    </Box>
  );
};

export default QualityAssessmentPage;
